<template>
  <!-- 费用报销：报销申请与审批流转。部门按申请人主部门自动带出 -->
  <div class="expense-claim-list">
    <list-page>
      <search-condition @search="resetSearch" :show-search="true" page-name="expenseClaimList">
        <template #default>
          <el-input v-model="searchInfo.searchKey" placeholder="单号/事由/申请人" @keydown.enter="resetSearch" clearable>
            <template #prefix>关键字</template>
          </el-input>
          <el-select v-model="searchInfo.expenseTypeId" placeholder="全部" clearable>
            <template #prefix>费用类型</template>
            <el-option v-for="t in typeList" :key="t.userCode" :label="t.dictName" :value="t.userCode"/>
          </el-select>
          <el-select v-model="searchInfo.claimStatus" placeholder="全部" clearable>
            <template #prefix>审批状态</template>
            <el-option v-for="s in STATUS_OPTIONS" :key="s.value" :label="s.label" :value="s.value"/>
          </el-select>
        </template>
        <template #option>
          <page-button type="read" el-type="primary" @click="resetSearch">查询</page-button>
          <page-button type="write" el-type="success" @click="openDialog()">+ 新建报销</page-button>
        </template>
      </search-condition>

      <!-- KPI -->
      <div class="stat-grid">
        <div class="stat-card">
          <div class="label">报销单数</div>
          <div class="value">{{ stat.claimCount || 0 }}<span class="unit">单</span></div>
        </div>
        <div class="stat-card">
          <div class="label">报销总金额</div>
          <div class="value money">{{ money(stat.totalAmount) }}</div>
        </div>
        <div class="stat-card">
          <div class="label">审批中</div>
          <div class="value" style="color:#e6a23c">{{ stat.approvingCount || 0 }}<span class="unit">单</span></div>
        </div>
        <div class="stat-card">
          <div class="label">已通过</div>
          <div class="value" style="color:#67c23a">{{ stat.passedCount || 0 }}<span class="unit">单</span></div>
        </div>
      </div>
      <!-- 口径说明：草稿不算发生额，但列表仍要展示，否则申请人没法继续改 -->
      <div class="tip-bar">
        口径说明：KPI 与费用类型分布只统计「已提交」的单据，草稿尚未提交、不计入发生额；
        列表仍会展示草稿，方便继续编辑与提交。
      </div>
      <!-- 费用类型分布 -->
      <div class="chart-card">
        <div class="section-title">费用类型分布（按金额）</div>
        <div ref="typeChart" class="chart-div"></div>
      </div>

      <list-table v-loading="loading" background-color="#FFF">
        <yq-table :data="dataList" :empty-text="'暂无数据'" height="100%">
          <yq-table-column label="报销单号" prop="claimNo" width="130">
            <template v-slot="{row}"><span class="num muted">{{ row.claimNo }}</span></template>
          </yq-table-column>
          <yq-table-column label="申请人" width="90">
            <template v-slot="{row}"><span class="cell-title">{{ row.applicantName || '—' }}</span></template>
          </yq-table-column>
          <yq-table-column label="所属部门" prop="departTitle" width="150rem" :show-overflow-tooltip="true"/>
          <yq-table-column label="费用类型" prop="expenseTypeId" align="center" width="100rem">
            <template v-slot="{row}">
              <el-tag size="small" :type="typeTagType(row.expenseTypeId)">{{ typeName(row.expenseTypeId) }}</el-tag>
            </template>
          </yq-table-column>
          <yq-table-column label="报销金额" prop="claimAmount" align="right" width="130rem">
            <template v-slot="{row}"><span class="num bold">{{ money(row.claimAmount) }}</span></template>
          </yq-table-column>
          <!-- 关联客户（非必填）。客户名由后端按 partner_id 批量查回后组装，不 join -->
          <yq-table-column label="客户" prop="partnerName" width="140rem" :show-overflow-tooltip="true"/>
          <yq-table-column label="费用说明" prop="claimReason" min-width="180rem" :show-overflow-tooltip="true"/>
          <yq-table-column label="发生日期" prop="occurDate" width="110rem">
            <template v-slot="{row}"><span class="num">{{ dateOnly(row.occurDate) }}</span></template>
          </yq-table-column>
          <yq-table-column label="申请状态" prop="claimStatus" align="center" width="95rem">
            <template v-slot="{row}">
              <el-tag size="small" :type="statusTagType(row.claimStatus)">{{ statusName(row.claimStatus) }}</el-tag>
            </template>
          </yq-table-column>

            <!-- 审批状态：由工作流回写，与申请状态是两回事（见 ProcessFlowConstant.ApprovalStatus） -->
            <yq-table-column label="审批状态" prop="approvalStatus" align="center" width="95rem">
              <template v-slot="{row}">
                <el-tag size="small" :type="approvalTagType(row.approvalStatus)">
                  {{ approvalName(row.approvalStatus) }}
                </el-tag>
              </template>
            </yq-table-column>
          <yq-table-column label="操作" prop="options" align="center" fixed="right" width="250" :show-overflow-tooltip="false">
            <template v-slot="{row}">
              <!--
                驳回/中止/撤回后申请状态都会回到草稿，与「从没提交过的新草稿」
                在申请状态上无从区分。按钮文案按审批状态区分：前者该「重新提交」，
                后者才是「编辑」—— 否则用户看不出这张单子已经被打回过。
              -->
              <table-button v-if="isResubmit(row)" @click="openDialog(row)">重新提交</table-button>
              <table-button v-else-if="isDraft(row.claimStatus)" @click="openDialog(row)">编辑</table-button>
              <table-button v-if="row.claimStatus === STATUS.APPROVING" @click="withdrawRow(row)">撤回</table-button>
              <table-button v-if="isDraft(row.claimStatus)" @click="deleteRow(row)">删除</table-button>
              <!-- 审批走过流程的单据才能查看流转记录；没走过的不显示入口 -->
              <table-button v-if="row.approvalStatus" @click="openFlowLog(row)">查看审批</table-button>
            </template>
          </yq-table-column>
        </yq-table>
      </list-table>
      <form-page v-model:page-number="searchInfo.pageNumber" v-model:page-size="searchInfo.pageSize"
                 :total-count="searchInfo.totalCount" @onLoad="queryList"/>
    </list-page>

    <!-- 新建/编辑报销单 -->
    <el-dialog v-model="showDialog" :title="dialogData.id ? '编辑报销单' : '新建报销单'" dialog-width="2" :close-on-click-modal="false">
      <form-items :data="dialogData" :columns="2" label-width="3rem" icon-width="0" ref="dialogForm">
        <form-item label="费用类型" :required="true" name="expenseTypeId" error-message="请选择费用类型">
          <el-select v-model="dialogData.expenseTypeId" placeholder="请选择" style="width: 100%">
            <el-option v-for="t in typeList" :key="t.userCode" :label="t.dictName" :value="t.userCode"/>
          </el-select>
        </form-item>
        <form-item label="报销金额(元)" :required="true" name="claimAmount" error-message="请填写报销金额">
          <el-input-number v-model="dialogData.claimAmount" :min="0" :precision="2" :controls="false" style="width: 100%"/>
        </form-item>
        <form-item label="发生日期" :required="true" name="occurDate" error-message="请选择费用发生日期">
          <el-date-picker v-model="dialogData.occurDate" type="date" value-format="YYYY-MM-DD" style="width: 100%"/>
        </form-item>
        <form-item label="所属部门">
          <!-- 服务端按申请人主部门带出，这里只读展示 -->
          <el-input :model-value="dialogData.mainDepartName" disabled placeholder="按申请人主部门自动带出"/>
        </form-item>
        <form-item label="关联客户">
          <!-- 非必填：这笔费用属于哪个客户。存的是往来单位档案id，与客户选择组件的取值口径一致 -->
          <customer-select v-model="dialogData.partnerId" placeholder="请选择客户（非必填）"/>
        </form-item>
        <form-item label="费用说明" :required="true" name="claimReason" error-message="请填写费用说明" :show-block="true">
          <el-input v-model="dialogData.claimReason" type="textarea" :rows="3"
                    :placeholder="`写明费用用途，便于审批（不少于 ${REASON_MIN_LENGTH} 个字）`"/>
        </form-item>
        <form-item label="备注" :show-block="true">
          <el-input v-model="dialogData.remark" type="textarea" :rows="2" placeholder="选填"/>
        </form-item>
        <!-- 附件：多张图片，存逗号分隔的 URL 串；审批时会在单据详情里一并展示 -->
        <form-item label="附件" :show-block="true">
          <image-upload v-model="dialogData.attachUrl" :limit="6" folder="expense"/>
        </form-item>
      </form-items>
      <template #footer>
        <page-button @click="showDialog = false">取消</page-button>
        <page-button type="write" @click="saveData">保存草稿</page-button>
        <page-button type="write" el-type="primary" @click="submitData">提交</page-button>
      </template>
    </el-dialog>

    <!-- 审批流转记录：让申请人看到自己单据的审批过程（谁批的、什么意见、什么时候） -->
    <el-drawer v-model="showFlowLog" title="审批流转记录" size="60%" :destroy-on-close="true">
      <div v-loading="flowLogLoading" class="flow-log-wrap">
        <flow-log-panel :detail="flowLogDetail"
                        :empty-text="flowLogEmptyText"/>
      </div>
    </el-drawer>
  </div>
</template>

<script>
import * as echarts from "echarts";
import {markRaw} from "vue";
import ListPage from "@/components/h5/layout/ListPage.vue";
import ListTable from "@/components/h5/layout/ListTable.vue";
import SearchCondition from "@/components/h5/layout/SearchCondition.vue";
import FormPage from "@/components/h5/layout/FormPage.vue";
import FormItems from "@/components/h5/layout/FormItems.vue";
import FormItem from "@/components/h5/layout/FormItem.vue";
import PageButton from "@/components/h5/button/PageButton.vue";
import TableButton from "@/components/h5/button/TableButton.vue";
import ImageUpload from "@/components/h5/image/ImageUpload.vue";
import CustomerSelect from "@/components/h5/partner/CustomerSelect.vue";
import FlowLogPanel from "@/views/h5/common/approval/FlowLogPanel.vue";
import YqTable from "@/components/h5/table/YqTable.vue";
import expenseClaimApi from "@/api/dz-crm/expense/expenseClaimApi";
import commonApi from "@/api/commonApi";
import Util from "@/util/util";
import {useRoute, useRouter} from "vue-router";

const STATUS = {DRAFT: 0, APPROVING: 100, PASSED: 200, REJECTED: 300};
const STATUS_OPTIONS = [
  {value: STATUS.DRAFT, label: "草稿"},
  {value: STATUS.APPROVING, label: "审批中"},
  {value: STATUS.PASSED, label: "已通过"},
  {value: STATUS.REJECTED, label: "已驳回"},
];
const STATUS_TAG = {0: "info", 100: "warning", 200: "success", 300: "danger"};
/** 费用类型标签色，按字典顺序循环取，字典可增删所以不写死映射 */
const TYPE_COLORS = ["", "warning", "danger", "success", "info"];
/** 审批状态（工作流维度），与后端 ProcessFlowConstant.ApprovalStatus 对齐 */
const APPROVAL_OPTIONS = [
  {value: 0, label: "未开始"},
  {value: 100, label: "审批中"},
  {value: 200, label: "已中止"},
  {value: 300, label: "已完成"},
  {value: 400, label: "已撤回"},
  {value: 500, label: "已驳回"},
];



/**
 * 费用说明的最少字数
 *
 * 按字符数算（去首尾空格后 length），不做「中文算一个字、英文算半个」的区分 ——
 * 报销事由基本都是中文，那种区分只会让规则难以预期。
 */
const REASON_MIN_LENGTH = 10;

export default {
  setup() {
    return {router: useRouter(), route: useRoute()};
  },
  name: "ExpenseClaimList",
  components: {
    YqTable,
    ListPage, ListTable, SearchCondition, FormPage, FormItems, FormItem,
    PageButton, TableButton, ImageUpload, FlowLogPanel, CustomerSelect,
  },
  data() {
    return {
      STATUS,
      STATUS_OPTIONS,
      // 模板里要用来拼 placeholder，必须在这里暴露一次 ——
      // 模块顶层的 const 不是组件属性，模板访问不到（会渲染成 undefined）。
      // 这也是上面 STATUS / STATUS_OPTIONS 放在 data 里的同一个原因。
      REASON_MIN_LENGTH,
      loading: false,
      // 审批流转记录抽屉
      showFlowLog: false,
      flowLogLoading: false,
      flowLogDetail: null,
      flowLogEmptyText: "该单据尚未发起审批",
      searchInfo: {
        pageNumber: 1, pageSize: 20, searchKey: "",
        expenseTypeId: "", claimStatus: "",
      },
      dataList: [],
      stat: {},
      typeList: [],
      userInfo: {},
      showDialog: false,
      dialogData: {},
      typeChart: null,
    };
  },
  computed: {

  },
  beforeRouteLeave(to, from, next) {
    // Util.cachePageData(this, to, from);
    next();
  },
  async mounted() {
    this.userInfo = await Util.getUserInfo();
    // 费用类型是一致性字典（type = EXPENSE_TYPE），取值 userCode / 显示 dictName
    commonApi.queryUniqueDictionaryList("EXPENSE_TYPE").then(list => this.typeList = list || []);
    this.resetSearch();
  },
  unmounted() {
    if (this.typeChart) {
      this.typeChart.dispose();
    }
  },
  methods: {
    /**
     * 查看该单据的审批流转记录。
     *
     * <p>按 businessType + 单据 id 反查流程实例 —— 业务页手里只有自己的单据 id，
     * 没有流程实例 id。查不到说明这张单子没走过审批（草稿或工作流未启用），
     * 给出明确提示而不是弹一个空抽屉。
     */
    async openFlowLog(row) {
      this.showFlowLog = true;
      this.flowLogLoading = true;
      this.flowLogDetail = null;
      this.flowLogEmptyText = "该单据尚未发起审批";
      try {
        // 走本页自己的接口，业务类型由服务端写死 —— 不要再调审批中心的 checkApi
        const detail = await expenseClaimApi.queryFlowInstance(row.id);
        this.flowLogDetail = detail || null;
        if (!detail) {
          this.flowLogEmptyText = "该单据尚未发起审批";
        }
      } catch (e) {
        this.flowLogEmptyText = "审批记录加载失败，请稍后重试";
        this.$message.warning("审批记录加载失败");
      } finally {
        this.flowLogLoading = false;
      }
    },
    /**
     * 是否是「被打回后待重新提交」的草稿。
     *
     * <p>驳回/中止/撤回都会让申请状态回到草稿，光看申请状态与新建草稿无异。
     * 需要靠审批状态才能看出它经历过一次审批，按钮因此显示「重新提交」。
     */
    isResubmit(row) {
      const s = row.approvalStatus;
      return s === 500 || s === 200 || s === 400;
    },

    /**
     * 审批状态名。
     *
     * <p>与申请状态是两回事：申请状态回答「这张单子走到哪一步」，
     * 审批状态回答「它的审批走到哪一步」。取值口径见后端 ApprovalStatus。
     */
    approvalName(status) {
      const hit = APPROVAL_OPTIONS.find(o => o.value === status);
      return hit ? hit.label : "未开始";
    },
    /** 审批状态标签色：进行中橙、完成绿、中止红、未开始灰 */
    approvalTagType(status) {
      if (status === 100) {
        return "warning";
      }
      if (status === 300) {
        return "success";
      }
      if (status === 500) {
        // 已驳回用橙色：它需要申请人去改，是个待办信号
        return "warning";
      }
      if (status === 200) {
        return "danger";
      }
      // 未开始与已撤回都用灰：都不是「出了事」，只是「还没在跑」
      return "info";
    },
    async queryList() {
      this.loading = true;
      try {
        const {records, totalCount} = await expenseClaimApi.queryList(this.cleanParams(this.searchInfo));
        this.dataList = records || [];
        this.searchInfo.totalCount = totalCount;
      } finally {
        this.loading = false;
      }
    },
    resetSearch() {
      this.searchInfo.pageNumber = 1;
      this.queryList();
      this.loadStat();
    },
    /** 空值不提交，避免空串绑到后端 Integer 报错 */
    cleanParams(source) {
      const params = {};
      Object.keys(source).forEach(k => {
        const v = source[k];
        if (v === "" || v === null || v === undefined) {
          return;
        }
        params[k] = v;
      });
      return params;
    },
    /**
     * KPI 与费用类型分布。
     * 调本页自己的 stat 接口（ExpenseClaimController），不跨 controller 调报表接口 ——
     * 同页面只调一个 controller 是接口管理要求。
     */
    async loadStat() {
      const data = await expenseClaimApi.stat(this.cleanParams(this.searchInfo)) || {};
      this.stat = data;
      // 类型名走字典关联 —— 后端只返回 dimKey
      const named = (data.typeRows || []).map(r =>
          ({name: this.typeName(r.dimKey), value: Number(r.totalAmount || 0)}));
      this.$nextTick(() => this.renderChart(named));
    },
    renderChart(data) {
      const dom = this.$refs.typeChart;
      if (!dom) {
        return;
      }
      if (!this.typeChart) {
        this.typeChart = markRaw(echarts.init(dom));
      }
      this.typeChart.setOption({
        tooltip: {trigger: "item", appendToBody: true, formatter: "{b}：{c} 元"},
        legend: {bottom: 0, type: "scroll"},
        series: [{
          type: "pie", radius: ["45%", "68%"], center: ["50%", "45%"],
          label: {formatter: "{b}\n{c} 元"},
          data: data,
        }],
      }, true);
    },
    /*
     * 匹配一律用 userCode，不是字典 id。
     * 单据里存的是 userCode（如 TRAVEL）—— 它是稳定的业务编码，
     * 而 id 是主键，重新导入字典就会变，存量单据立刻失去关联。
     */
    typeName(typeUserCode) {
      const t = this.typeList.find(x => x.userCode === typeUserCode);
      return t ? t.dictName : "";
    },
    typeTagType(typeUserCode) {
      const index = this.typeList.findIndex(x => x.userCode === typeUserCode);
      if (index < 0) {
        return undefined;
      }
      const color = TYPE_COLORS[index % TYPE_COLORS.length];
      return color === "" ? undefined : color;
    },
    statusName(status) {
      const s = STATUS_OPTIONS.find(x => x.value === status);
      return s ? s.label : "";
    },
    statusTagType(status) {
      return STATUS_TAG[status];
    },
    isDraft(status) {
      return status === STATUS.DRAFT;
    },
    dateOnly(value) {
      return value ? String(value).slice(0, 10) : "—";
    },
    money(value) {
      if (value === null || value === undefined) {
        return "0.00";
      }
      return Number(value).toLocaleString("zh-CN", {minimumFractionDigits: 2, maximumFractionDigits: 2});
    },
    /**
     * 打开新增/编辑弹框
     *
     * ★ 新增时必须先向后端申请一个编码当 id —— 工作流拦截器要从 body 里
     *   取 biz_key_field(id) 的值去查流程实例，id 必须先于提交存在。
     *   单号 claimNo 与 id 共用这个值，不再由后端另外生成。
     */
    async openDialog(row) {
      if (row) {
        this.dialogData = JSON.parse(JSON.stringify(row));
      } else {
        const today = this.today();
        let id = "";
        try {
          id = await expenseClaimApi.applyCode();
        } catch (e) {
          this.$message.error("申请单号失败，请稍后重试");
          return;
        }
        this.dialogData = {
          id: id, claimNo: id,
          occurDate: today,
          mainDepartName: this.userInfo.mainDepartName,
          departCode: this.userInfo.mainDepartCode,
        };
      }
      this.showDialog = true;
    },
    today() {
      const d = new Date();
      const p = (n) => String(n).padStart(2, "0");
      return d.getFullYear() + "-" + p(d.getMonth() + 1) + "-" + p(d.getDate());
    },
    /**
     * 保存草稿。
     * ★ 必须显式传 claimStatus：工作流拦截器要从 body 里读这个字段的值
     *   来判断是否发起流程，不传的话它读到 null 会直接放行。
     */
    async saveData() {
      if (!this.checkForm()) {
        return;
      }
      // 部门由服务端按申请人主部门带出，这里不提交 departCode
      const payload = Object.assign({}, this.dialogData, {claimStatus: STATUS.DRAFT});
      await expenseClaimApi.saveDraft(payload);
      this.showDialog = false;
      this.resetSearch();
    },

    /**
     * 表单校验：表单组件自带的必填校验 + 费用说明的字数要求
     *
     * 保存草稿与提交共用同一套校验 —— 两条路径本该收同样的约束，
     * 各自写一遍迟早会出现「草稿能存、提交报错」这种分叉。
     *
     * ★ 字数按 trim 之后的字符数算，防止用户敲空格凑够 10 个字。
     */
    checkForm() {
      if (!this.$refs.dialogForm.validCheck()) {
        return false;
      }
      const reason = (this.dialogData.claimReason || "").trim();
      if (reason.length < REASON_MIN_LENGTH) {
        this.$message.error(`费用说明不少于 ${REASON_MIN_LENGTH} 个字，当前 ${reason.length} 个`);
        return false;
      }
      return true;
    },
    /**
     * 提交：保存当前表单并直接进入审批流程。
     * 与保存草稿共用同一套校验收敛，区别只在服务端最后把状态置为审批中。
     */
    submitData() {
      if (!this.checkForm()) {
        return;
      }
      // ★ claimStatus 必须随 body 一起传：它同时是工作流的 status_trigger，
      //   拦截器从 body 读它来决定是否发起流程
      const payload = Object.assign({}, this.dialogData, {claimStatus: STATUS.APPROVING});
      this.$confirm("提交后进入审批流程，提交期间不可编辑，确认提交？", "提示", {type: "warning"})
          .then(() => expenseClaimApi.saveSubmit(payload))
          .then(() => {
            this.showDialog = false;
            this.resetSearch();
          })
          .catch(() => {});
    },
    withdrawRow(row) {
      this.$confirm("撤回后报销单退回草稿，需重新提交，确认撤回？", "提示", {type: "warning"})
          .then(() => expenseClaimApi.withdrawClaim(row.id))
          .then(() => this.resetSearch())
          .catch(() => {});
    },
    deleteRow(row) {
      this.$confirm("确认删除这张报销单？", "提示", {type: "warning"})
          .then(() => expenseClaimApi.delete(row.id))
          .then(() => this.resetSearch())
          .catch(() => {});
    },
  },
};
</script>

<style lang="scss" scoped>
.stat-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;
  margin-bottom: 14px;

  .stat-card {
    border: 1px solid var(--yq-border-color, #ebeef5);
    border-radius: 6px;
    padding: 12px 16px;
    background: #fff;

    .label {
      font-size: 12px;
      color: #909399;
    }

    .value {
      font-size: 24px;
      font-weight: 700;
      height: 34px;
      line-height: 34px;
      margin: 4px 0;

      &.money {
        font-size: 20px;
      }

      .unit {
        font-size: 14px;
        font-weight: 400;
        margin-left: 2px;
      }
    }
  }
}

.tip-bar {
  font-size: 12px;
  color: #909399;
  line-height: 1.7;
  background: #fafbfc;
  border: 1px solid var(--yq-border-color, #ebeef5);
  border-radius: 6px;
  padding: 10px 14px;
  margin-bottom: 12px;
}

.chart-card {
  border: 1px solid var(--yq-border-color, #ebeef5);
  border-radius: 6px;
  padding: 12px 16px;
  margin-bottom: 12px;
  background: #fff;

  .section-title {
    font-size: 14px;
    font-weight: 600;
    color: #303133;
    margin-bottom: 10px;
  }

  .chart-div {
    height: 280px;
    width: 100%;
  }
}

.cell-title {
  color: #303133;
  font-weight: 500;
}

.num {
  font-variant-numeric: tabular-nums;
}

.muted {
  color: #909399;
}

.bold {
  font-weight: 600;
  color: #303133;
}
</style>
